/* eslint-disable complexity */
// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";

import { media } from "@atlaskit/primitives/responsive";

import { ExportableAdgColorTypes, getColor } from "./adg-colors";
import { getGradient, ValidAngles } from "./gradients";

const positioner = {
    cornerTopLeft: "top left",
    cornerTopRight: "top right",
    cornerBottomLeft: "bottom left",
    cornerBottomRight: "bottom right",
    sideTop: "center top",
    sideLeft: "left center",
    sideRight: "right center",
    sideBottom: "center bottom",
    hidden: "-100% -100%"
};

const sizes = {
    desktop: {
        cornerTopLeft: "400px auto",
        cornerTopRight: "400px auto",
        cornerBottomLeft: "400px auto",
        cornerBottomRight: "400px auto",
        sideTop: "auto 150px",
        sideLeft: "400px auto",
        sideRight: "400px auto",
        sideBottom: "auto 150px",
        hidden: "0 0"
    },
    tablet: {
        cornerTopLeft: "auto 50%",
        cornerTopRight: "auto 50%",
        cornerBottomLeft: "auto 50%",
        cornerBottomRight: "auto 50%",
        sideTop: "auto 150px",
        sideLeft: "calc(50% - 350px) auto",
        sideRight: "calc(50% - 350px) auto",
        sideBottom: "auto 150px",
        hidden: "0 0"
    },
    mobile: {
        cornerTopLeft: "auto 200px",
        cornerTopRight: "auto 200px",
        cornerBottomLeft: "auto 200px",
        cornerBottomRight: "auto 200px",
        sideTop: "auto 150px",
        sideLeft: "50%",
        sideRight: "50%",
        sideBottom: "auto 200px",
        hidden: "0 0"
    }
};

export type ValidPositions =
    | "cornerTopLeft"
    | "cornerTopRight"
    | "cornerBottomLeft"
    | "cornerBottomRight"
    | "sideTop"
    | "sideLeft"
    | "sideRight"
    | "sideBottom"
    | "hidden";

export interface PositionProperties {
    imageLink: string;
    positionDesktop: ValidPositions;
    positionTablet: ValidPositions;
    positionMobile: ValidPositions;
}

export interface BGProps {
    isCompiled?: boolean; // TODO: Doesn't have support for partialBackgrounds
    bgColor?: ExportableAdgColorTypes;
    textColor?: ExportableAdgColorTypes;
    gradientDirection?: ValidAngles;
    bgColor2?: ExportableAdgColorTypes;
    bgImage?: string;
    partialBackgrounds?: PositionProperties[];
}

const getBGObject = (bg: PositionProperties) => {
    return {
        src: `url(${bg.imageLink})`,
        posDesktop: positioner[bg.positionDesktop],
        posTablet: positioner[bg.positionTablet],
        posMobile: positioner[bg.positionMobile],
        sizeDesktop: sizes.desktop[bg.positionDesktop],
        sizeTablet: sizes.tablet[bg.positionTablet],
        sizeMobile: sizes.mobile[bg.positionMobile]
    };
};

export const BGStyles = (backgroundProperties?: BGProps) => {
    const obj = backgroundProperties ? backgroundProperties : {};
    const hasGradient = obj.gradientDirection && obj.bgColor && obj.bgColor2;

    const totalBGs = [];
    const collections = {
        desktop: {
            topCorners: false,
            bottomCorners: false
        },
        tablet: {
            topCorners: false,
            bottomCorners: false
        },
        mobile: {
            topCorners: false,
            bottomCorners: false
        }
    };

    if (hasGradient) {
        totalBGs.unshift({
            src: getGradient(obj.gradientDirection, obj.bgColor, obj.bgColor2),
            posDesktop: "top left",
            posTablet: "top left",
            posMobile: "top left",
            sizeDesktop: "100% 100%",
            sizeTablet: "100% 100%",
            sizeMobile: "100% 100%"
        });
    }

    if (obj.bgImage) {
        totalBGs.unshift({
            src: `url(${obj.bgImage} )`,
            posDesktop: "center center",
            posTablet: "center center",
            posMobile: "center center",
            sizeDesktop: "cover",
            sizeTablet: "cover",
            sizeMobile: "cover"
        });
    }

    if (obj.isCompiled) {
        const bgContent =
            hasGradient || obj.bgImage || obj.partialBackgrounds
                ? `
                      background-image: ${totalBGs
                          .map((bg) => bg.src)
                          .join(", ")};
                      background-repeat: ${totalBGs
                          .map(() => "no-repeat")
                          .join(", ")};

                      @media only screen and (min-width: 1440px) : {
                          background-size: ${totalBGs
                              .map((bg) => bg.sizeDesktop)
                              .join(", ")};
                          background-position: ${totalBGs
                              .map((bg) => bg.posDesktop)
                              .join(", ")};
                      }
                      @media only screen and (min-width: 768px) and (max-width: 1439.98px) : {
                          background-size: ${totalBGs
                              .map((bg) => bg.sizeTablet)
                              .join(", ")};
                          background-position: ${totalBGs
                              .map((bg) => bg.posTablet)
                              .join(", ")};
                      }
                      @media only screen and (max-width: 767.98px) : {
                          background-size: ${totalBGs
                              .map((bg) => bg.sizeMobile)
                              .join(", ")};
                          background-position: ${totalBGs
                              .map((bg) => bg.posMobile)
                              .join(", ")};
                      }
                  `
                : "";

        return css`
            background-color: ${obj.bgColor
                ? getColor(obj.bgColor)
                : "transparent"};
            color: ${obj.textColor ? getColor(obj.textColor) : "#091e42"};
            ${bgContent}
        `;
    }

    if (obj.partialBackgrounds) {
        obj.partialBackgrounds.forEach((bg) => {
            totalBGs.unshift(getBGObject(bg));
            if (
                bg.positionDesktop.startsWith("cornerTop") ||
                bg.positionDesktop == "sideTop"
            ) {
                collections.desktop.topCorners = true;
            }
            if (
                bg.positionDesktop.startsWith("cornerBottom") ||
                bg.positionDesktop == "sideBottom"
            ) {
                collections.desktop.bottomCorners = true;
            }
            if (
                bg.positionTablet.startsWith("cornerTop") ||
                bg.positionTablet == "sideTop"
            ) {
                collections.tablet.topCorners = true;
            }
            if (
                bg.positionTablet.startsWith("cornerBottom") ||
                bg.positionTablet == "sideBottom"
            ) {
                collections.tablet.bottomCorners = true;
            }
            if (
                bg.positionMobile.startsWith("cornerTop") ||
                bg.positionMobile == "sideTop"
            ) {
                collections.mobile.topCorners = true;
            }
            if (
                bg.positionMobile.startsWith("cornerBottom") ||
                bg.positionMobile == "sideBottom"
            ) {
                collections.mobile.bottomCorners = true;
            }
        });
    }

    const backgroundImage = `
      background-image: ${totalBGs.map((bg) => bg.src).join(", ")};
      background-size: ${totalBGs.map((bg) => bg.sizeMobile).join(", ")};
      background-position: ${totalBGs.map((bg) => bg.posMobile).join(", ")};
      background-repeat: ${totalBGs.map(() => "no-repeat").join(", ")};
      ${collections.mobile.topCorners ? `padding-top: 200px;` : ""}
      ${collections.mobile.bottomCorners ? `padding-bottom: 200px;` : ""}

      ${media.above.sm} {
          background-image: ${totalBGs.map((bg) => bg.src).join(", ")};
          background-size: ${totalBGs.map((bg) => bg.sizeTablet).join(", ")};
          background-position: ${totalBGs.map((bg) => bg.posTablet).join(", ")};
          background-repeat: ${totalBGs.map(() => "no-repeat").join(", ")};
          ${collections.tablet.topCorners ? `padding-top: 120px;` : ""}
          ${collections.tablet.bottomCorners ? `padding-bottom: 120px;` : ""}
      }

      ${media.above.lg} {
          background-image: ${totalBGs.map((bg) => bg.src).join(", ")};
          background-size: ${totalBGs.map((bg) => bg.sizeDesktop).join(", ")};
          background-position: ${totalBGs
              .map((bg) => bg.posDesktop)
              .join(", ")};
          background-repeat: ${totalBGs.map(() => "no-repeat").join(", ")};
          ${collections.desktop.topCorners ? `padding-top: 120px;` : ""}
          ${collections.desktop.bottomCorners ? `padding-bottom: 120px;` : ""}
      }



    `;

    return css`
        background-color: ${obj.bgColor
            ? getColor(obj.bgColor)
            : "transparent"};
        color: ${obj.textColor ? getColor(obj.textColor) : "#091e42"};
        ${hasGradient || obj.bgImage || obj.partialBackgrounds
            ? `${backgroundImage}`
            : ""}
    `;
};
